import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';
import { theme } from '../theme';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import AuthLayout from '../components/layout/AuthLayout';
import { signIn } from '../firebase/auth';
import toast from 'react-hot-toast';

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const ForgotPasswordLink = styled(Link)`
  font-size: ${theme.typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.8);
  text-decoration: none;
  text-align: right;
  transition: color 0.2s ease-in-out;

  &:hover {
    color: ${theme.colors.white};
    text-decoration: underline;
  }
`;

interface LoginFormData {
    email: string;
    password: string;
}

const LoginPage: React.FC = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);

    const {
        register,
        handleSubmit,
        formState: { errors }
    } = useForm<LoginFormData>();

    const onSubmit = async (data: LoginFormData) => {
        try {
            setLoading(true);
            await signIn(data.email, data.password);
            
            // Store password temporarily in sessionStorage to restore session after creating staff
            // This is needed because creating a new user automatically signs them in, logging out the admin
            // Note: This is stored in sessionStorage (cleared on browser close) for security
            sessionStorage.setItem('admin_password_temp', data.password);
            sessionStorage.setItem('admin_email_temp', data.email);
            
            toast.success('Login successful!');
            navigate('/dashboard');
        } catch (error: any) {
            console.error('Login error:', error);
            toast.error(error.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <AuthLayout
            title="Welcome Back"
            subtitle="Sign in to your account to access the security portal"
        >
            <Form onSubmit={handleSubmit(onSubmit)}>
                <FormGroup>
                    <Input
                        label="Email Address"
                        type="email"
                        placeholder="Enter your email"
                        required
                        {...register('email', {
                            required: 'Email is required',
                            pattern: {
                                value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                message: 'Invalid email address'
                            }
                        })}
                        error={errors.email?.message}
                    />
                </FormGroup>

                <FormGroup>
                    <Input
                        label="Password"
                        type="password"
                        placeholder="Enter your password"
                        required
                        {...register('password', {
                            required: 'Password is required',
                            minLength: {
                                value: 6,
                                message: 'Password must be at least 6 characters'
                            }
                        })}
                        error={errors.password?.message}
                    />
                    <ForgotPasswordLink to="/forgot-password">
                        Forgot your password?
                    </ForgotPasswordLink>
                </FormGroup>

                <Button
                    type="submit"
                    variant="primary"
                    size="lg"
                    fullWidth
                    loading={loading}
                    disabled={loading}
                >
                    Sign In
                </Button>
            </Form>
        </AuthLayout>
    );
};

export default LoginPage;
